import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, PowerOff, Tablet, Sparkles, Wifi, WifiOff } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtDateTime, useAppTz } from "@/lib/tz";

/**
 * Connected Devices — single pane of glass for every active iPad session
 * across the fleet. Cabin kiosks and Spa Ops kiosks are listed with device
 * label, IP, connected-at, and a per-session Kick button. Only Developer /
 * Admin can kick; Manager / Supervisor see the panel read-only.
 */
export default function ConnectedDevicesPanel() {
  const { admin } = useAuth();
  const tz = useAppTz();
  const canKick = admin?.role === "developer" || admin?.role === "admin";
  const [data, setData] = useState({ cabin_ipads: [], spa_ops: [], server_at: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [kicking, setKicking] = useState(null); // token being kicked, disables its button

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    try {
      const { data: r } = await api.get("/admin/connected-devices");
      setData(r || { cabin_ipads: [], spa_ops: [] });
    } catch (e) {
      // Refresh runs every 12 s. On a transient error, stay silent — the previous
      // dataset stays on screen and the next tick will retry. Only toast on the
      // initial (non-refresh) failure so operators know something's actually up.
      if (!silent) {
        toast.error(formatErr(e?.response?.data?.detail) || "Could not load connected devices");
      } else {
        // eslint-disable-next-line no-console
        console.debug("[ConnectedDevices] silent refresh failed:", e?.message || e);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Auto-refresh every 12 s so freshly-connected iPads appear without a
    // manual reload; matches the poll cadence of the iPad kick watcher.
    const id = setInterval(() => load(true), 12000);
    return () => clearInterval(id);
  }, [load]);

  const kick = async (row, scope) => {
    const label = row.device_label || (scope === "spa_ops" ? "Spa Ops kiosk" : "iPad");
    const target = scope === "cabin_ipad" ? `${row.cabin_name} · ${label}` : `${row.user_email || "kiosk"} · ${label}`;
    if (!window.confirm(`Kick "${target}"? The device will be signed out immediately.`)) return;
    setKicking(row.token);
    try {
      await api.post("/admin/connected-devices/kick", {
        scope,
        token: row.token,
        cabin_id: scope === "cabin_ipad" ? row.cabin_id : undefined,
      });
      toast.success(`Kicked ${target}`);
      load(true);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not kick device");
    } finally {
      setKicking(null);
    }
  };

  const cabinTotal = (data.cabin_ipads || []).length;
  const spaOpsTotal = (data.spa_ops || []).length;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-display text-3xl">Connected devices</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Every iPad currently signed in to the fleet — cabin kiosks and Spa Ops kiosks.
            {canKick
              ? " Tap Kick to remotely sign a specific device out; other devices stay live."
              : " Contact an Admin to kick a device."}
            {" "}<span className="text-accent">Times shown in {tz}.</span>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(true)} disabled={loading || refreshing} data-testid="devices-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : (
        <div className="space-y-10">
          {/* --------- Cabin iPad sessions ---------- */}
          <section data-testid="cabin-ipads-section">
            <div className="flex items-baseline gap-3 mb-3">
              <h3 className="font-display text-xl">Cabin iPads</h3>
              <Badge variant="outline" className="text-[10px]">{cabinTotal}</Badge>
            </div>
            {cabinTotal === 0 ? (
              <div className="border border-dashed border-border p-6 text-sm text-muted-foreground" data-testid="no-cabin-ipads">
                No cabin iPads are signed in right now.
              </div>
            ) : (
              <div className="border border-border overflow-hidden bg-card">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left overline text-[10px] text-primary/60 border-b border-border">
                      <th className="px-4 py-3">Cabin</th>
                      <th className="px-4 py-3">Device</th>
                      <th className="px-4 py-3 hidden md:table-cell">IP</th>
                      <th className="px-4 py-3 hidden lg:table-cell">Connected</th>
                      <th className="px-4 py-3 hidden lg:table-cell">Last seen</th>
                      <th className="px-4 py-3 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cabin_ipads.map((row) => (
                      <tr key={row.token} className="border-b border-border/70 last:border-b-0 hover:bg-muted/30" data-testid={`cabin-ipad-row-${row.token}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 font-medium">
                            <Tablet className="h-3.5 w-3.5 text-primary/70" />
                            {row.cabin_name}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>{row.device_label}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{row.device_id?.slice(0, 12) || "—"}</div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell font-mono text-xs">{row.ip || "—"}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">{row.connected_at ? fmtDateTime(row.connected_at) : "—"}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">{row.last_seen_at ? fmtDateTime(row.last_seen_at) : "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {row.live ? (
                              <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-0 hover:bg-emerald-500/20 text-[10px]" data-testid={`live-${row.token}`}>
                                <Wifi className="h-3 w-3 mr-1" /> Live
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px]" data-testid={`idle-${row.token}`}>
                                <WifiOff className="h-3 w-3 mr-1" /> Idle
                              </Badge>
                            )}
                            {canKick && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={kicking === row.token}
                                onClick={() => kick(row, "cabin_ipad")}
                                className="text-destructive border-destructive/40"
                                data-testid={`kick-cabin-${row.token}`}
                              >
                                <PowerOff className="h-3 w-3 mr-1" />
                                {kicking === row.token ? "Kicking…" : "Kick"}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* --------- Spa Ops sessions ---------- */}
          <section data-testid="spa-ops-section">
            <div className="flex items-baseline gap-3 mb-3">
              <h3 className="font-display text-xl">Spa Ops kiosks</h3>
              <Badge variant="outline" className="text-[10px]">{spaOpsTotal}</Badge>
            </div>
            {spaOpsTotal === 0 ? (
              <div className="border border-dashed border-border p-6 text-sm text-muted-foreground" data-testid="no-spa-ops">
                No Spa Ops kiosks are signed in right now.
              </div>
            ) : (
              <div className="border border-border overflow-hidden bg-card">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left overline text-[10px] text-primary/60 border-b border-border">
                      <th className="px-4 py-3">Account</th>
                      <th className="px-4 py-3">Device</th>
                      <th className="px-4 py-3 hidden md:table-cell">IP</th>
                      <th className="px-4 py-3 hidden lg:table-cell">Connected</th>
                      <th className="px-4 py-3 hidden lg:table-cell">Last seen</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.spa_ops.map((row) => (
                      <tr key={row.token} className="border-b border-border/70 last:border-b-0 hover:bg-muted/30" data-testid={`spa-ops-row-${row.token}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 font-medium">
                            <Sparkles className="h-3.5 w-3.5 text-accent" />
                            <span className="truncate">{row.user_email}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>{row.device_label}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{row.device_id?.slice(0, 12) || "—"}</div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell font-mono text-xs">{row.ip || "—"}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">{row.connected_at ? fmtDateTime(row.connected_at) : "—"}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">{row.last_seen_at ? fmtDateTime(row.last_seen_at) : "—"}</td>
                        <td className="px-4 py-3 text-right">
                          {canKick ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={kicking === row.token}
                              onClick={() => kick(row, "spa_ops")}
                              className="text-destructive border-destructive/40"
                              data-testid={`kick-spa-ops-${row.token}`}
                            >
                              <PowerOff className="h-3 w-3 mr-1" />
                              {kicking === row.token ? "Kicking…" : "Kick"}
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">Read-only</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
